import * as yup from "yup";
import i18next from "i18next";

interface PhoneNumberRule {
  min: number;
  max: number;
  example: string;
}

export const PHONE_NUMBER_RULES: Record<string, PhoneNumberRule> = {
  "+48": { min: 9, max: 9, example: "512345678" },
  "+49": { min: 7, max: 11, example: "15123456789" },
  "+420": { min: 9, max: 9, example: "602123456" },
  "+421": { min: 9, max: 9, example: "915123456" },
  "+372": { min: 7, max: 8, example: "51234567" },
  "+370": { min: 8, max: 8, example: "61234567" },
};

export type PhoneNumberCheck = "valid" | "invalid" | "leadingZero";

const hasValidLength = (digits: string, rule: PhoneNumberRule) =>
  /^[1-9][0-9]*$/.test(digits) && digits.length >= rule.min && digits.length <= rule.max;

export const checkPhoneNumber = (prefix: string, number: string): PhoneNumberCheck => {
  const rule = PHONE_NUMBER_RULES[prefix];
  if (!rule) {
    return "invalid";
  }
  if (hasValidLength(number, rule)) {
    return "valid";
  }
  if (number.startsWith("0") && hasValidLength(number.slice(1), rule)) {
    return "leadingZero";
  }
  return "invalid";
};

export const phoneNumberMessage = (prefix: string, number: string, check: PhoneNumberCheck): string => {
  const rule = PHONE_NUMBER_RULES[prefix];
  if (!rule) {
    return i18next.t("auth.validation.phoneInvalid", { ns: "admin" });
  }
  if (check === "leadingZero") {
    return i18next.t("auth.validation.phoneLeadingZero", { ns: "admin", example: number.slice(1) });
  }
  const digits = rule.min === rule.max ? `${rule.min}` : `${rule.min}–${rule.max}`;
  return i18next.t("auth.validation.phoneLength", { ns: "admin", prefix, digits, example: rule.example });
};

export const phoneNumberSchema = (prefixField: string) =>
  yup.string().test("phone-number", "", function (value?: string | null) {
    if (!value) {
      return true;
    }
    const prefix = this.parent[prefixField];
    const check = checkPhoneNumber(prefix, value);
    return check === "valid" || this.createError({ message: phoneNumberMessage(prefix, value, check) });
  });
